import { Component, ElementRef, HostListener, Injector, afterNextRender, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { Client } from '@lr/core';

@Component({
  selector: 'app-client-selector',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './client-selector.component.html',
  styleUrl: './client-selector.component.scss'
})
export class ClientSelectorComponent {
  clients = input<Client[]>([]);
  selectedClientId = input<string | null>(null);
  clientName = input<string | null>(null);

  clientChange = output<string>();

  searchTerm = signal('');
  menuOpen = signal(false);
  private readonly searchThreshold = 7;

  filteredClients = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) {
      return this.clients();
    }
    return this.clients().filter(client => client.name.toLowerCase().includes(term));
  });

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private injector: Injector
  ) {}

  shouldShowSearch(): boolean {
    return this.clients().length > this.searchThreshold;
  }

  selectedName(): string {
    const selectedId = this.selectedClientId() || this.clients()[0]?.mCode || null;
    const client = this.clients().find(item => item.mCode === selectedId);
    return client?.name || this.clientName() || 'Client';
  }

  isSelected(mCode: string): boolean {
    const selectedId = this.selectedClientId() || this.clients()[0]?.mCode || null;
    return mCode === selectedId;
  }

  toggleMenu(): void {
    this.menuOpen.update(open => !open);
    if (!this.menuOpen()) {
      this.searchTerm.set('');
      return;
    }
    afterNextRender(() => this.keepMenuInViewport(), { injector: this.injector });
  }

  private keepMenuInViewport(): void {
    const menu = this.elementRef.nativeElement.querySelector('.client-menu');
    if (!(menu instanceof HTMLElement)) {
      return;
    }
    menu.style.transform = 'translateX(-50%)';
    const rect = menu.getBoundingClientRect();
    const padding = 12;
    let shift = 0;
    if (rect.right > window.innerWidth - padding) {
      shift -= rect.right - (window.innerWidth - padding);
    }
    if (rect.left + shift < padding) {
      shift += padding - (rect.left + shift);
    }
    if (shift !== 0) {
      menu.style.transform = `translateX(calc(-50% + ${shift}px))`;
    }
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }

  onSelect(mCode: string): void {
    this.clientChange.emit(mCode);
    this.searchTerm.set('');
    this.menuOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(event: MouseEvent): void {
    if (!this.menuOpen()) {
      return;
    }
    const target = event.target;
    if (target instanceof Node && this.elementRef.nativeElement.contains(target)) {
      return;
    }
    this.menuOpen.set(false);
    this.searchTerm.set('');
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    if (!this.menuOpen()) {
      return;
    }
    this.menuOpen.set(false);
    this.searchTerm.set('');
  }
}
